import React, { useState, useEffect, useCallback } from "react";
import {
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Info,
  Save,
  CalendarClock,
  Timer,
  Mail,
  Bell,
  Zap,
  Shield,
  FileSpreadsheet,
  FolderSearch,
  X,
  TriangleAlert,
  Link,
  Unlink,
  Power,
  PowerOff,
  History,
  ChevronDown,
  ChevronRight,
  Circle,
} from "lucide-react";
import {
  getExcelMonitorSchedule,
  updateExcelMonitorSchedule,
  ScheduleStatus,
  getExcelMonitorEmailConfig,
  updateExcelMonitorEmailConfig,
  EmailConfigStatus,
  getExcelMonitorFileConfig,
  updateExcelMonitorFileConfig,
  deleteExcelMonitorFileConfig,
  FileConfigStatus,
  GoogleDriveFile,
  checkGoogleDriveConnection,
  authenticateGoogleDrive,
  disconnectGoogleDrive,
  MonitorFileItem,
  listMonitorFiles,
  addMonitorFile,
  removeMonitorFile,
  toggleExcelMonitorSchedule,
  getExcelMonitorRunHistory,
  RunHistoryItem,
} from "../../../service/apiService";
import GoogleDriveFilePicker from "../../GoogleDrive/GoogleDriveFilePicker";
import GoogleDriveAuth from "../../GoogleDrive/GoogleDriveAuth";

// MIME types where Google creates a converted copy instead of editing the original.
// PDF and plain text files get duplicated when opened via Google Docs.
const NON_NATIVE_MIME_TYPES = new Set([
  "application/pdf",
  "text/plain",
]);

const isNonNativeFile = (mimeType?: string): boolean =>
  !!mimeType && NON_NATIVE_MIME_TYPES.has(mimeType);

type IntervalUnit = "minutes" | "hours" | "days";

enum LoadingState {
  IDLE = "idle",
  LOADING = "loading",
  SUCCESS = "success",
  ERROR = "error",
}

enum SaveState {
  IDLE = "idle",
  SAVING = "saving",
  SUCCESS = "success",
  ERROR = "error",
}

/* ────────────────────────────────────────────
   Hooks (unchanged from original)
   ──────────────────────────────────────────── */

const useExcelMonitorSchedule = () => {
  const [schedule, setSchedule] = useState<ScheduleStatus | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedule = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await getExcelMonitorSchedule();
      setSchedule(data);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      const errorMessage =
        err?.response?.status === 404
          ? "Schedule not found"
          : "Failed to load schedule configuration";
      setError(errorMessage);
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    schedule,
    setSchedule,
    loadingState,
    error,
    fetchSchedule,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

const useExcelMonitorEmailConfig = () => {
  const [emailConfig, setEmailConfig] = useState<EmailConfigStatus | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchEmailConfig = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await getExcelMonitorEmailConfig();
      setEmailConfig(data);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      const errorMessage =
        err?.response?.status === 404
          ? "Email config not found"
          : "Failed to load email configuration";
      setError(errorMessage);
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    emailConfig,
    setEmailConfig,
    loadingState,
    error,
    fetchEmailConfig,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

const useExcelMonitorFileConfig = () => {
  const [fileConfig, setFileConfig] = useState<FileConfigStatus | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchFileConfig = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await getExcelMonitorFileConfig();
      setFileConfig(data);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      const errorMessage =
        err?.response?.status === 404
          ? "File config not found"
          : "Failed to load file configuration";
      setError(errorMessage);
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    fileConfig,
    setFileConfig,
    loadingState,
    error,
    fetchFileConfig,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

const useMonitorFiles = () => {
  const [monitorFiles, setMonitorFiles] = useState<MonitorFileItem[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.LOADING);
  const [error, setError] = useState<string | null>(null);

  const fetchMonitorFiles = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await listMonitorFiles();
      setMonitorFiles(data.files);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      setError("Failed to load monitored files");
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    monitorFiles,
    setMonitorFiles,
    loadingState,
    error,
    fetchMonitorFiles,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

const useRunHistory = () => {
  const [runs, setRuns] = useState<RunHistoryItem[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.IDLE);
  const [error, setError] = useState<string | null>(null);

  const fetchRunHistory = useCallback(async () => {
    try {
      setLoadingState(LoadingState.LOADING);
      setError(null);
      const data = await getExcelMonitorRunHistory();
      setRuns(data.runs);
      setLoadingState(LoadingState.SUCCESS);
    } catch (err: any) {
      setError("Failed to load run history");
      setLoadingState(LoadingState.ERROR);
    }
  }, []);

  return {
    runs,
    loadingState,
    error,
    fetchRunHistory,
    isLoading: loadingState === LoadingState.LOADING,
  };
};

/* ────────────────────────────────────────────
   Shared UI Pieces
   ──────────────────────────────────────────── */

const PageLoadingSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded-xl w-64 mb-2" />
        <div className="h-4 bg-gray-100 rounded-lg w-96" />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-10 h-10 bg-gray-200 rounded-full" />
            <div className="space-y-2 flex-1">
              <div className="h-5 bg-gray-200 rounded w-48" />
              <div className="h-3 bg-gray-100 rounded w-72" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-100 rounded-xl" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SectionLoadingSkeleton: React.FC<{ color?: string }> = ({
  color = "blue",
}) => (
  <div className="animate-pulse px-5 py-4">
    <div className="flex items-center gap-4 mb-4">
      <div className={`w-10 h-10 bg-${color}-100 rounded-full`} />
      <div className="space-y-2 flex-1">
        <div className="h-4 bg-gray-200 rounded w-48" />
        <div className="h-3 bg-gray-100 rounded w-72" />
      </div>
    </div>
    <div className="space-y-3">
      <div className="h-10 bg-gray-100 rounded-xl" />
      <div className="h-10 bg-gray-100 rounded-xl" />
    </div>
  </div>
);

interface ErrorCardProps {
  message: string;
  onRetry: () => void;
}

const ErrorCard: React.FC<ErrorCardProps> = ({ message, onRetry }) => (
  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
    <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="text-sm text-red-700 mb-2">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center text-xs font-medium text-red-600 hover:text-red-700"
      >
        <RefreshCw className="h-3 w-3 mr-1" />
        Retry
      </button>
    </div>
  </div>
);

interface StatusPillProps {
  isEnabled: boolean;
}

const StatusPill: React.FC<StatusPillProps> = ({ isEnabled }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
      isEnabled
        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
        : "bg-gray-50 text-gray-500 border border-gray-200"
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        isEnabled ? "bg-emerald-500 animate-pulse" : "bg-gray-400"
      }`}
    />
    {isEnabled ? "Active" : "Inactive"}
  </span>
);

/* ────────────────────────────────────────────
   Step Component – collapsible accordion step
   ──────────────────────────────────────────── */

type StepStatus = "complete" | "current" | "incomplete" | "error";

interface StepProps {
  stepNumber: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: StepStatus;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  accentColor: string;
}

const Step: React.FC<StepProps> = ({
  stepNumber,
  title,
  description,
  icon,
  status,
  isOpen,
  onToggle,
  children,
  accentColor,
}) => {
  const statusIcon = () => {
    switch (status) {
      case "complete":
        return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case "current":
        return (
          <div className={`w-5 h-5 rounded-full border-2 border-${accentColor}-500 flex items-center justify-center`}>
            <div className={`w-2 h-2 rounded-full bg-${accentColor}-500`} />
          </div>
        );
      default:
        return <Circle className="h-5 w-5 text-gray-300" />;
    }
  };

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all duration-200 ${
        isOpen ? `border-${accentColor}-200 shadow-md` : "border-gray-100"
      }`}
    >
      {/* Step Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50/50 transition-colors"
      >
        {/* Step Number Badge */}
        <div
          className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
            status === "complete"
              ? "bg-emerald-100 text-emerald-700"
              : isOpen
              ? `bg-violet-100 text-violet-700`
              : "bg-gray-100 text-gray-500"
          }`}
        >
          {status === "complete" ? (
            <CheckCircle2 className="h-4.5 w-4.5" />
          ) : (
            stepNumber
          )}
        </div>

        {/* Title + Desc */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3
              className={`text-sm font-semibold ${
                isOpen ? "text-gray-900" : "text-gray-700"
              }`}
            >
              {title}
            </h3>
            {status === "complete" && !isOpen && (
              <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                Done
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{description}</p>
        </div>

        {/* Expand icon */}
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {/* Step Content */}
      {isOpen && (
        <div className="px-5 pb-5 pt-1 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────
   Main Component
   ──────────────────────────────────────────── */

const EventBridgeSchedule: React.FC = () => {
  // ── Schedule hook ──
  const {
    schedule,
    setSchedule,
    loadingState: scheduleLoadingState,
    error: scheduleError,
    fetchSchedule,
    isLoading: isScheduleLoading,
  } = useExcelMonitorSchedule();

  const [intervalValue, setIntervalValue] = useState<number>(5);
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>("minutes");
  const [saveState, setSaveState] = useState<SaveState>(SaveState.IDLE);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // ── Email hook ──
  const {
    emailConfig,
    setEmailConfig,
    loadingState: emailLoadingState,
    error: emailError,
    fetchEmailConfig,
    isLoading: isEmailLoading,
  } = useExcelMonitorEmailConfig();

  const [senderEmail, setSenderEmail] = useState<string>("");
  const [recipientEmail, setRecipientEmail] = useState<string>("");
  const [emailSaveState, setEmailSaveState] = useState<SaveState>(SaveState.IDLE);
  const [emailSaveError, setEmailSaveError] = useState<string | null>(null);
  const [hasEmailChanges, setHasEmailChanges] = useState(false);

  // ── File config hook ──
  const {
    fileConfig,
    setFileConfig,
    loadingState: fileLoadingState,
    error: fileError,
    fetchFileConfig,
    isLoading: isFileLoading,
  } = useExcelMonitorFileConfig();

  // ── Monitor files hook ──
  const {
    monitorFiles,
    setMonitorFiles,
    loadingState: monitorFilesLoadingState,
    error: monitorFilesError,
    fetchMonitorFiles,
    isLoading: isMonitorFilesLoading,
  } = useMonitorFiles();

  // ── Run history hook ──
  const {
    runs,
    loadingState: runHistoryLoadingState,
    error: runHistoryError,
    fetchRunHistory,
    isLoading: isRunHistoryLoading,
  } = useRunHistory();

  // ── Google Drive connection ──
  const [isDriveConnected, setIsDriveConnected] = useState(false);
  const [checkingDrive, setCheckingDrive] = useState(true);
  const [showGDriveAuth, setShowGDriveAuth] = useState(false);

  // ── File picker ──
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [fileSaveState, setFileSaveState] = useState<SaveState>(SaveState.IDLE);
  const [fileSaveError, setFileSaveError] = useState<string | null>(null);

  // ── Toggle monitor ──
  const [toggleLoading, setToggleLoading] = useState(false);

  // ── Wizard step management ──
  const [openStep, setOpenStep] = useState<number>(1);

  /* ── Effects ── */

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    if (schedule) {
      setIntervalValue(schedule.interval_value);
      setIntervalUnit(schedule.interval_unit as IntervalUnit);
    }
  }, [schedule]);

  useEffect(() => {
    if (!schedule) return;
    setHasChanges(
      intervalValue !== schedule.interval_value ||
        intervalUnit !== schedule.interval_unit
    );
  }, [intervalValue, intervalUnit, schedule]);

  useEffect(() => {
    if (saveState === SaveState.SUCCESS || saveState === SaveState.ERROR) {
      const t = setTimeout(() => setSaveState(SaveState.IDLE), 3000);
      return () => clearTimeout(t);
    }
  }, [saveState]);

  useEffect(() => {
    fetchEmailConfig();
  }, [fetchEmailConfig]);

  useEffect(() => {
    if (emailConfig) {
      setSenderEmail(emailConfig.ses_sender_email);
      setRecipientEmail(emailConfig.ses_recipient_email);
    }
  }, [emailConfig]);

  useEffect(() => {
    if (!emailConfig) return;
    setHasEmailChanges(
      senderEmail !== emailConfig.ses_sender_email ||
        recipientEmail !== emailConfig.ses_recipient_email
    );
  }, [senderEmail, recipientEmail, emailConfig]);

  useEffect(() => {
    if (emailSaveState === SaveState.SUCCESS || emailSaveState === SaveState.ERROR) {
      const t = setTimeout(() => setEmailSaveState(SaveState.IDLE), 3000);
      return () => clearTimeout(t);
    }
  }, [emailSaveState]);

  useEffect(() => {
    fetchFileConfig();
  }, [fetchFileConfig]);
  useEffect(() => {
    fetchMonitorFiles();
  }, [fetchMonitorFiles]);

  useEffect(() => {
    const checkDrive = async () => {
      setCheckingDrive(true);
      const connected = await checkGoogleDriveConnection();
      setIsDriveConnected(connected);
      setCheckingDrive(false);
    };
    checkDrive();
  }, []);

  useEffect(() => {
    if (fileSaveState === SaveState.SUCCESS || fileSaveState === SaveState.ERROR) {
      const t = setTimeout(() => setFileSaveState(SaveState.IDLE), 3000);
      return () => clearTimeout(t);
    }
  }, [fileSaveState]);

  /* ── Handlers (unchanged logic) ── */

  const handleFileSelect = useCallback(
    async (file: GoogleDriveFile) => {
      try {
        setFileSaveState(SaveState.SAVING);
        setFileSaveError(null);
        const result = await addMonitorFile(file.id, file.name, file.mimeType);
        setMonitorFiles((prev) => [
          ...prev,
          {
            google_drive_file_id: result.google_drive_file_id,
            file_name: result.file_name,
            file_mime_type: result.file_mime_type,
            connected_user_id: result.connected_user_id,
            updated_at: result.updated_at,
          },
        ]);
        setFileSaveState(SaveState.SUCCESS);
      } catch (err: any) {
        setFileSaveError(
          err?.response?.data?.detail || "Failed to add file. Please try again."
        );
        setFileSaveState(SaveState.ERROR);
      }
    },
    [setMonitorFiles]
  );

  const handleRemoveMonitorFile = useCallback(
    async (fileId: string) => {
      try {
        setFileSaveState(SaveState.SAVING);
        setFileSaveError(null);
        await removeMonitorFile(fileId);
        setMonitorFiles((prev) =>
          prev.filter((f) => f.google_drive_file_id !== fileId)
        );
        setFileSaveState(SaveState.SUCCESS);
      } catch (err: any) {
        setFileSaveError(
          err?.response?.data?.detail || "Failed to remove file. Please try again."
        );
        setFileSaveState(SaveState.ERROR);
      }
    },
    [setMonitorFiles]
  );

  const handleSave = useCallback(async () => {
    if (!hasChanges || saveState === SaveState.SAVING) return;
    try {
      setSaveState(SaveState.SAVING);
      setSaveError(null);
      const updated = await updateExcelMonitorSchedule(
        intervalValue,
        intervalUnit
      );
      setSchedule((prev) =>
        prev
          ? {
              ...prev,
              interval_value: updated.interval_value,
              interval_unit: updated.interval_unit,
              schedule_expression: updated.schedule_expression,
            }
          : prev
      );
      setSaveState(SaveState.SUCCESS);
      setHasChanges(false);
    } catch (err: any) {
      setSaveError(
        err?.response?.data?.detail ||
          "Failed to update schedule. Please try again."
      );
      setSaveState(SaveState.ERROR);
    }
  }, [hasChanges, saveState, intervalValue, intervalUnit, setSchedule]);

  const handleEmailSave = useCallback(async () => {
    if (!hasEmailChanges || emailSaveState === SaveState.SAVING) return;
    try {
      setEmailSaveState(SaveState.SAVING);
      setEmailSaveError(null);
      const payload: {
        ses_sender_email?: string;
        ses_recipient_email?: string;
      } = {};
      if (senderEmail !== emailConfig?.ses_sender_email)
        payload.ses_sender_email = senderEmail;
      if (recipientEmail !== emailConfig?.ses_recipient_email)
        payload.ses_recipient_email = recipientEmail;
      const updated = await updateExcelMonitorEmailConfig(payload);
      setEmailConfig((prev) =>
        prev
          ? {
              ...prev,
              ses_sender_email: updated.ses_sender_email,
              ses_recipient_email: updated.ses_recipient_email,
              updated_at: updated.updated_at,
            }
          : prev
      );
      setEmailSaveState(SaveState.SUCCESS);
      setHasEmailChanges(false);
    } catch (err: any) {
      setEmailSaveError(
        err?.response?.data?.detail ||
          "Failed to update email config. Please try again."
      );
      setEmailSaveState(SaveState.ERROR);
    }
  }, [
    hasEmailChanges,
    emailSaveState,
    senderEmail,
    recipientEmail,
    emailConfig,
    setEmailConfig,
  ]);

  const handleToggleMonitor = useCallback(async () => {
    if (!schedule || toggleLoading) return;
    try {
      setToggleLoading(true);
      const newEnabled = !schedule.is_enabled;
      const updated = await toggleExcelMonitorSchedule(newEnabled);
      setSchedule((prev) =>
        prev
          ? {
              ...prev,
              is_enabled: updated.is_enabled,
            }
          : prev
      );
    } catch (err: any) {
      console.error("Failed to toggle monitor:", err);
    } finally {
      setToggleLoading(false);
    }
  }, [schedule, toggleLoading, setSchedule]);

  const handleGDriveAuthSuccess = useCallback(
    async (code: string) => {
      try {
        await authenticateGoogleDrive(code);
        setIsDriveConnected(true);
      } catch (err) {
        console.error("Google Drive auth error:", err);
      }
    },
    []
  );

  const refreshAll = () => {
    fetchSchedule();
    fetchEmailConfig();
    fetchFileConfig();
    fetchMonitorFiles();
  };

  /* ── Derived state ── */

  const anyLoading =
    isScheduleLoading && isEmailLoading && isFileLoading;

  if (anyLoading) {
    return <PageLoadingSkeleton />;
  }

  const ratePreview = `rate(${intervalValue} ${
    intervalValue === 1 ? intervalUnit.replace(/s$/, "") : intervalUnit
  })`;

  // Step completion status
  const driveStepStatus: StepStatus = isDriveConnected
    ? "complete"
    : checkingDrive
    ? "incomplete"
    : "incomplete";

  const filesStepStatus: StepStatus =
    monitorFiles.length > 0 ? "complete" : "incomplete";

  const emailStepStatus: StepStatus =
    emailConfig && emailConfig.ses_recipient_email ? "complete" : "incomplete";

  const scheduleStepStatus: StepStatus =
    schedule ? "complete" : scheduleLoadingState === LoadingState.ERROR ? "error" : "incomplete";

  const monitorStepStatus: StepStatus = schedule
    ? "complete"
    : "incomplete";

  /* ── Format helpers ── */

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "changed":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            Changed
          </span>
        );
      case "unchanged":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
            No change
          </span>
        );
      case "error":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Error
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
            {status || "Unknown"}
          </span>
        );
    }
  };

  /* ════════════════════════════════════════════
     RENDER
     ════════════════════════════════════════════ */

  return (
    <div className="min-h-screen bg-gradient-to-br p-6">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* ── Page Header ── */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Google Drive Monitor
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Set up file monitoring in a few steps
            </p>
          </div>
          <div className="flex items-center gap-2">
            {schedule && <StatusPill isEnabled={schedule.is_enabled} />}
            <button
              onClick={refreshAll}
              disabled={isScheduleLoading || isEmailLoading || isFileLoading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  isScheduleLoading || isEmailLoading ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════
           STEP 1 – Connect Google Drive
           ═══════════════════════════════════ */}
        <Step
          stepNumber={1}
          title="Connect Google Drive"
          description={
            isDriveConnected
              ? "Your Google Drive account is connected"
              : "Authorize Kyva to access your Google Drive files"
          }
          icon={<Link className="h-5 w-5" />}
          status={driveStepStatus}
          isOpen={openStep === 1}
          onToggle={() => setOpenStep(openStep === 1 ? 0 : 1)}
          accentColor="violet"
        >
          {checkingDrive ? (
            <div className="flex items-center gap-3 py-4">
              <RefreshCw className="h-4 w-4 text-violet-500 animate-spin" />
              <span className="text-sm text-gray-500">
                Verifying connection...
              </span>
            </div>
          ) : isDriveConnected ? (
            <div className="space-y-4 pt-3">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">
                    Google Drive Connected
                  </p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    Your account is linked and ready for file monitoring
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowGDriveAuth(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Manage Connection
                </button>
                <button
                  onClick={() => setOpenStep(2)}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                >
                  Next Step
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-3">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Connection Required
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Connect your Google Drive to select files for monitoring.
                    Kyva needs access to read file changes.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowGDriveAuth(true)}
                className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all shadow-sm"
              >
                <Link className="h-4 w-4 mr-2" />
                Connect Google Drive
              </button>
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 2 – Select Files
           ═══════════════════════════════════ */}
        <Step
          stepNumber={2}
          title="Select Files to Monitor"
          description={
            monitorFiles.length > 0
              ? `${monitorFiles.length} file${monitorFiles.length !== 1 ? "s" : ""} selected`
              : "Choose Google Drive files to watch for changes"
          }
          icon={<FileSpreadsheet className="h-5 w-5" />}
          status={filesStepStatus}
          isOpen={openStep === 2}
          onToggle={() => setOpenStep(openStep === 2 ? 0 : 2)}
          accentColor="emerald"
        >
          {isMonitorFilesLoading ? (
            <SectionLoadingSkeleton color="emerald" />
          ) : monitorFilesLoadingState === LoadingState.ERROR ? (
            <ErrorCard
              message={monitorFilesError ?? "Unexpected error"}
              onRetry={fetchMonitorFiles}
            />
          ) : (
            <div className="space-y-4 pt-3">
              {/* File List */}
              {monitorFiles.length > 0 ? (
                <div className="space-y-2">
                  {monitorFiles.map((file) => (
                    <div
                      key={file.google_drive_file_id}
                      className={`rounded-xl p-3 ${
                        isNonNativeFile(file.file_mime_type)
                          ? "bg-amber-50 border border-amber-200"
                          : "bg-emerald-50 border border-emerald-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileSpreadsheet
                            className={`h-4 w-4 flex-shrink-0 ${
                              isNonNativeFile(file.file_mime_type)
                                ? "text-amber-600"
                                : "text-emerald-600"
                            }`}
                          />
                          <div className="min-w-0">
                            <p
                              className={`text-sm font-medium truncate ${
                                isNonNativeFile(file.file_mime_type)
                                  ? "text-amber-900"
                                  : "text-emerald-900"
                              }`}
                            >
                              {file.file_name || "Unknown file"}
                            </p>
                            <p
                              className={`text-xs font-mono truncate ${
                                isNonNativeFile(file.file_mime_type)
                                  ? "text-amber-600"
                                  : "text-emerald-600"
                              }`}
                            >
                              ID: {file.google_drive_file_id}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            handleRemoveMonitorFile(file.google_drive_file_id)
                          }
                          disabled={fileSaveState === SaveState.SAVING}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                          title="Remove monitored file"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {isNonNativeFile(file.file_mime_type) && (
                        <div className="flex items-start gap-2 mt-2 pt-2 border-t border-amber-200">
                          <TriangleAlert className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700 leading-relaxed">
                            <span className="font-semibold">
                              Limited monitoring:
                            </span>{" "}
                            This file type is not natively editable in Google
                            Drive. Changes may not be detected.
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-6 text-center">
                  <FolderSearch className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    No files selected. Choose files from Google Drive to monitor.
                  </p>
                </div>
              )}

              {/* Save feedback */}
              {fileSaveState === SaveState.ERROR && fileSaveError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{fileSaveError}</span>
                </div>
              )}

              {fileSaveState === SaveState.SUCCESS && (
                <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>File list updated successfully!</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowFilePicker(true)}
                  disabled={
                    !isDriveConnected || fileSaveState === SaveState.SAVING
                  }
                  className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                    !isDriveConnected
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                      : fileSaveState === SaveState.SAVING
                      ? "bg-emerald-400 text-white cursor-not-allowed"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  }`}
                >
                  {fileSaveState === SaveState.SAVING ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FolderSearch className="h-4 w-4 mr-2" />
                      Add File from Drive
                    </>
                  )}
                </button>

                {!isDriveConnected && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                    <AlertCircle className="h-3 w-3" />
                    Connect Google Drive first (Step 1)
                  </span>
                )}

                {isDriveConnected && monitorFiles.length > 0 && (
                  <button
                    onClick={() => setOpenStep(3)}
                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                  >
                    Next Step
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                )}
              </div>
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 3 – Set Recipient Email
           ═══════════════════════════════════ */}
        <Step
          stepNumber={3}
          title="Set Recipient Email"
          description={
            emailConfig?.ses_recipient_email
              ? `Notifications sent to ${emailConfig.ses_recipient_email}`
              : "Configure who receives change alerts"
          }
          icon={<Mail className="h-5 w-5" />}
          status={emailStepStatus}
          isOpen={openStep === 3}
          onToggle={() => setOpenStep(openStep === 3 ? 0 : 3)}
          accentColor="purple"
        >
          {isEmailLoading ? (
            <SectionLoadingSkeleton color="purple" />
          ) : emailLoadingState === LoadingState.ERROR ? (
            <ErrorCard
              message={emailError ?? "Unexpected error"}
              onRetry={fetchEmailConfig}
            />
          ) : (
            <div className="space-y-4 pt-3">
              {/* SES Notice */}
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <Shield className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  Recipient email addresses must be{" "}
                  <strong>verified in AWS SES</strong> before use. Unverified
                  emails will be rejected.
                </p>
              </div>

              {/* Email Input */}
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">
                  Recipient Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="recipient@example.com"
                    className="block w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition-all duration-200 placeholder-gray-400"
                  />
                </div>
                <p className="text-xs text-gray-400">
                  Receives alerts when file changes are detected
                </p>
              </div>

              {/* Save Error */}
              {emailSaveState === SaveState.ERROR && emailSaveError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{emailSaveError}</span>
                </div>
              )}

              {/* Action Row */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleEmailSave}
                  disabled={
                    !hasEmailChanges || emailSaveState === SaveState.SAVING
                  }
                  className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                    emailSaveState === SaveState.SAVING
                      ? "bg-violet-400 text-white cursor-not-allowed"
                      : emailSaveState === SaveState.SUCCESS
                      ? "bg-emerald-500 text-white"
                      : !hasEmailChanges
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                      : "bg-violet-600 text-white hover:bg-violet-700"
                  }`}
                >
                  {emailSaveState === SaveState.SAVING ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : emailSaveState === SaveState.SUCCESS ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Email
                    </>
                  )}
                </button>

                {hasEmailChanges && emailSaveState === SaveState.IDLE && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}

                {emailConfig?.ses_recipient_email && !hasEmailChanges && (
                  <button
                    onClick={() => setOpenStep(4)}
                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                  >
                    Next Step
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                )}
              </div>
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 4 – Set Schedule
           ═══════════════════════════════════ */}
        <Step
          stepNumber={4}
          title="Set Monitor Schedule"
          description={
            schedule
              ? `Checking every ${schedule.interval_value} ${schedule.interval_unit}`
              : "Define how often files are checked for changes"
          }
          icon={<CalendarClock className="h-5 w-5" />}
          status={scheduleStepStatus}
          isOpen={openStep === 4}
          onToggle={() => setOpenStep(openStep === 4 ? 0 : 4)}
          accentColor="blue"
        >
          {isScheduleLoading ? (
            <SectionLoadingSkeleton color="blue" />
          ) : scheduleLoadingState === LoadingState.ERROR ? (
            <ErrorCard
              message={scheduleError ?? "Unexpected error"}
              onRetry={fetchSchedule}
            />
          ) : (
            <div className="space-y-4 pt-3">
              {/* Current Schedule Stats */}
              {schedule && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Clock className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                        Interval
                      </span>
                    </div>
                    <p className="text-lg font-bold text-blue-900">
                      {schedule.interval_value}
                      <span className="text-xs font-medium text-blue-600 ml-1">
                        {schedule.interval_unit}
                      </span>
                    </p>
                  </div>

                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Timer className="h-3.5 w-3.5 text-indigo-500" />
                      <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">
                        Expression
                      </span>
                    </div>
                    <p className="text-xs font-bold text-indigo-900 font-mono truncate">
                      {schedule.schedule_expression}
                    </p>
                  </div>

                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Zap className="h-3.5 w-3.5 text-slate-500" />
                      <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                        Timezone
                      </span>
                    </div>
                    <p className="text-xs font-bold text-slate-900">
                      {schedule.timezone}
                    </p>
                  </div>
                </div>
              )}

              {/* Update Form */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Update Check Frequency
                </label>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <label className="block text-xs text-gray-500 mb-1">
                      Every
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={1440}
                      value={intervalValue}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val) && val > 0 && val <= 1440) {
                          setIntervalValue(val);
                        }
                      }}
                      className="w-24 px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all"
                    />
                  </div>
                  <div className="flex-shrink-0">
                    <label className="block text-xs text-gray-500 mb-1">
                      Unit
                    </label>
                    <select
                      value={intervalUnit}
                      onChange={(e) =>
                        setIntervalUnit(e.target.value as IntervalUnit)
                      }
                      className="w-32 px-3 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-900 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all cursor-pointer"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                  <div className="flex-1 pt-4">
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                      <Info className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                      <code className="text-xs font-mono text-gray-600">
                        {ratePreview}
                      </code>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Error */}
              {saveState === SaveState.ERROR && saveError && (
                <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{saveError}</span>
                </div>
              )}

              {/* Action Row */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || saveState === SaveState.SAVING}
                  className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl transition-all shadow-sm ${
                    saveState === SaveState.SAVING
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : saveState === SaveState.SUCCESS
                      ? "bg-emerald-500 text-white"
                      : !hasChanges
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {saveState === SaveState.SAVING ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : saveState === SaveState.SUCCESS ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Saved!
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Schedule
                    </>
                  )}
                </button>

                {hasChanges && saveState === SaveState.IDLE && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}

                {schedule && !hasChanges && (
                  <button
                    onClick={() => setOpenStep(5)}
                    className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                  >
                    Next Step
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </button>
                )}
              </div>
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 5 – Enable / Disable Monitor
           ═══════════════════════════════════ */}
        <Step
          stepNumber={5}
          title="Enable or Disable Monitor"
          description={
            schedule?.is_enabled
              ? "Monitor is currently active and checking for changes"
              : "Monitor is currently paused"
          }
          icon={
            schedule?.is_enabled ? (
              <Power className="h-5 w-5" />
            ) : (
              <PowerOff className="h-5 w-5" />
            )
          }
          status={monitorStepStatus}
          isOpen={openStep === 5}
          onToggle={() => setOpenStep(openStep === 5 ? 0 : 5)}
          accentColor="emerald"
        >
          {isScheduleLoading ? (
            <SectionLoadingSkeleton color="emerald" />
          ) : !schedule ? (
            <div className="py-4 text-sm text-gray-500">
              Schedule not loaded. Please configure the schedule first.
            </div>
          ) : (
            <div className="space-y-4 pt-3">
              {/* Toggle Card */}
              <div
                className={`rounded-xl p-5 border ${
                  schedule.is_enabled
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-xl ${
                        schedule.is_enabled
                          ? "bg-emerald-100"
                          : "bg-gray-200"
                      }`}
                    >
                      {schedule.is_enabled ? (
                        <Power className="h-6 w-6 text-emerald-600" />
                      ) : (
                        <PowerOff className="h-6 w-6 text-gray-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-base font-semibold text-gray-900">
                        File Monitor
                      </p>
                      <p className="text-sm text-gray-500">
                        {schedule.is_enabled
                          ? `Active - checking every ${schedule.interval_value} ${schedule.interval_unit}`
                          : "Paused - no files are being checked"}
                      </p>
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <button
                    onClick={handleToggleMonitor}
                    disabled={toggleLoading}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      schedule.is_enabled
                        ? "bg-emerald-500 focus:ring-emerald-400"
                        : "bg-gray-300 focus:ring-gray-400"
                    } ${toggleLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    role="switch"
                    aria-checked={schedule.is_enabled}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                        schedule.is_enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    >
                      {toggleLoading && (
                        <RefreshCw className="h-3 w-3 text-gray-400 animate-spin m-1" />
                      )}
                    </span>
                  </button>
                </div>
              </div>

              {/* Summary when enabled */}
              {schedule.is_enabled && (
                <div className="bg-white border border-gray-100 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Monitor Summary
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Files:</span>{" "}
                      <span className="font-medium text-gray-900">
                        {monitorFiles.length}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Interval:</span>{" "}
                      <span className="font-medium text-gray-900">
                        {schedule.interval_value} {schedule.interval_unit}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Email:</span>{" "}
                      <span className="font-medium text-gray-900 truncate">
                        {emailConfig?.ses_recipient_email || "Not set"}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>{" "}
                      <StatusPill isEnabled={schedule.is_enabled} />
                    </div>
                  </div>
                </div>
              )}

              {schedule && (
                <button
                  onClick={() => {
                    setOpenStep(6);
                    fetchRunHistory();
                  }}
                  className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700 transition-all"
                >
                  View Run History
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}
            </div>
          )}
        </Step>

        {/* ═══════════════════════════════════
           STEP 6 – Run History
           ═══════════════════════════════════ */}
        <Step
          stepNumber={6}
          title="Run History"
          description="View recent monitoring runs and their results"
          icon={<History className="h-5 w-5" />}
          status={runs.length > 0 ? "complete" : "incomplete"}
          isOpen={openStep === 6}
          onToggle={() => {
            setOpenStep(openStep === 6 ? 0 : 6);
            if (openStep !== 6) fetchRunHistory();
          }}
          accentColor="slate"
        >
          <div className="space-y-4 pt-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Recent file check results
              </p>
              <button
                onClick={fetchRunHistory}
                disabled={isRunHistoryLoading}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${
                    isRunHistoryLoading ? "animate-spin" : ""
                  }`}
                />
                Refresh
              </button>
            </div>

            {isRunHistoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 text-gray-400 animate-spin mr-2" />
                <span className="text-sm text-gray-500">
                  Loading run history...
                </span>
              </div>
            ) : runHistoryLoadingState === LoadingState.ERROR ? (
              <ErrorCard
                message={runHistoryError ?? "Failed to load run history"}
                onRetry={fetchRunHistory}
              />
            ) : runs.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-8 text-center">
                <History className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  No run history available yet.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  History will appear after the monitor runs for the first time.
                </p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        File
                      </th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Last Checked
                      </th>
                      <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {runs.map((run, index) => (
                      <tr
                        key={`${run.file_id}-${index}`}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="font-medium text-gray-900 truncate max-w-[200px]">
                              {run.file_name || run.file_id}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {formatDateTime(run.last_checked)}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(run.status)}
                          {run.error_message && (
                            <p className="text-xs text-red-500 mt-1 truncate max-w-[200px]">
                              {run.error_message}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Step>
      </div>

      {/* ── Modals ── */}
      <GoogleDriveAuth
        isOpen={showGDriveAuth}
        onClose={() => {
          setShowGDriveAuth(false);
          // Re-check connection after modal closes
          checkGoogleDriveConnection().then(setIsDriveConnected);
        }}
        onAuthSuccess={handleGDriveAuthSuccess}
      />

      <GoogleDriveFilePicker
        isOpen={showFilePicker}
        onClose={() => setShowFilePicker(false)}
        onFileSelect={handleFileSelect}
      />
    </div>
  );
};

export default EventBridgeSchedule;
